import {
	Component,
	DestroyRef,
	ElementRef,
	HostListener,
	OnInit,
	ViewChild,
	effect,
	inject,
	signal
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, style, transition, trigger } from '@angular/animations';
import { NgIconComponent } from '@ng-icons/core';
import {
	phosphorX,
	phosphorBooks,
	phosphorBookOpenUser,
	phosphorSidebar,
	phosphorGear,
	phosphorChatsCircle,
	phosphorBook,
	phosphorTrophy,
	phosphorLightning
} from '@ng-icons/phosphor-icons/regular';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuService } from '../../../services/menu.service';
import { ChatService } from '../../../services/chat.service';
import { LoadingService } from '../../../services/loading.service';
import { ParamsService } from '../../../services/params.service';
import { MobileService } from '../../../services/mobile.service';
import { MessagesService } from '../../../services/messages.service';
import { PathsService } from '../../../services/paths.service';
import { appRoutesType } from '../../../app.routes';
import { injectSpaConfig } from '../../../shared/utils/spaconfig';
import { containerAnimation } from '../../../shared/animations/container.animations';
import { UNIVERSITIES } from '../../../shared/consts/universities.const';
import { ChatList } from '../../../models/api/chats.model';
import { MenuActionType } from '../../../models/app/menu-action.model';
import { UserPreferencesComponent } from '../../user-preferences/user-preferences.component';
import { ChatCardComponent } from '../../chat-card/chat-card.component';
import { ProgressRouteComponent } from '../../progress-route/progress-route.component';

@Component({
	selector: 'unir-ai-main-menu',
	standalone: true,
	imports: [
		CommonModule,
		ButtonModule,
		TranslateModule,
		CheckboxModule,
		FormsModule,
		NgIconComponent,
		UserPreferencesComponent,
		ChatCardComponent,
		ProgressRouteComponent
	],
	templateUrl: './main-menu.component.html',
	styleUrls: ['./main-menu.component.scss'],
	animations: [
		containerAnimation,
		trigger('sectionAnimation', [
			transition(':enter', [style({ opacity: 0 }), animate('200ms ease-in', style({ opacity: 1 }))]),
			transition(':leave', [style({ opacity: 1 }), animate('0ms ease-out', style({ opacity: 0 }))])
		])
	]
})
export class MainMenuComponent implements OnInit {
	@ViewChild('scrollContainer') scrollableElement?: ElementRef;
	@HostListener('window:resize')
	onResize() {
		const element = this.scrollableElement?.nativeElement;
		if (element) {
			this.scroll.set(
				element.scrollHeight > element.clientHeight && element.scrollTop + element.clientHeight < element.scrollHeight
			);
		}
	}

	readonly #messageService = inject(MessagesService);

	spaConfig = injectSpaConfig();

	protected mobileService = inject(MobileService);
	protected menuService = inject(MenuService);
	protected router = inject(Router);
	protected loadingService = inject(LoadingService);
	protected chatService = inject(ChatService);
	protected destroyRef = inject(DestroyRef);
	protected paramsService = inject(ParamsService);
	protected pathsService = inject(PathsService);

	private location = inject(Location);

	scroll = signal(false);
	showUserPreferences = signal(false);
	section = signal<'route' | 'chat' | undefined>(undefined);
	currentWeek?: number;

	private subjectEffect = effect(
		() => {
			this.getPaths(this.paramsService.subjectId());
		},
		{ allowSignalWrites: true }
	);

	private topicsEffect = effect(
		() => {
			this.getChats(this.paramsService.topicIds());
		},
		{ allowSignalWrites: true }
	);

	menuActionType = MenuActionType;
	dataUNI = UNIVERSITIES;
	topicsSelected: number[] = [];
	topicsName: string[] = [];
	showTopic = false;
	icons: Record<string, string> = {
		books: phosphorBooks,
		topics: phosphorBookOpenUser,
		sidebar: phosphorSidebar,
		close: phosphorX,
		settings: phosphorGear,
		routes: phosphorLightning,
		chats: phosphorChatsCircle,
		book: phosphorBook,
		trophy: phosphorTrophy
	};

	ngOnInit() {
		this.listenQueryParams();
		this.listenMenuService();
	}

	openChat(chatInfo: { chatId: number; personalityId: number }) {
		if (this.mobileService.mobileView()) {
			this.menuService.mainMenuClosed.set(true);
		}
		const url = this.router
			.createUrlTree([this.paramsService.subjectId(), appRoutesType.CHAT, chatInfo.chatId])
			.toString();
		this.location.replaceState(url);
		this.menuService.editAction({ action: this.menuActionType.OPEN_CHAT, value: chatInfo.chatId.toString() });
		this.chatService.personalityId.set(chatInfo.personalityId);
	}

	openNewChat() {
		if (this.mobileService.mobileView()) {
			this.menuService.mainMenuClosed.set(true);
		}
		this.menuService.editAction({ action: this.menuActionType.RESET_CHAT });
	}

	renameChat(chatInfo: { id: number; name: string }) {
		this.chatService
			.renameChat(chatInfo.id, chatInfo.name)
			.pipe(
				catchError(() => {
					this.#messageService.sendMessageTranslate({
						type: 'error',
						title: 'ERRORS.CHAT.RENAME',
						text: 'ERRORS.CLOSE_RETRY'
					});
					return of(null);
				})
			)
			.subscribe();
	}

	removeChat(chatId: number) {
		this.menuService.chatList.update((current) => {
			if (!current) return current;
			return {
				...current,
				chats: current.chats.filter((chat: any) => chat.chatId !== chatId)
			};
		});
		if (chatId === this.paramsService.chatId()) {
			this.menuService.editAction({ action: this.menuActionType.RESET_CHAT });
		}
		this.chatService.removeChat(chatId).subscribe();
	}

	goHome() {
		if (this.menuService.paths()?.weeks.length) {
			this.goRouteSection();
		} else {
			this.goChatSection();
		}
	}

	goRouteSection() {
		this.menuService.showTheme.set(false);
		if (this.mobileService.mobileView()) {
			this.menuService.mainMenuClosed.set(true);
		}
		this.getPaths(this.paramsService.subjectId(), true);
	}

	goChatSection() {
		this.menuService.showTheme.set(false);
		if (this.mobileService.mobileView()) {
			this.menuService.mainMenuClosed.set(true);
		}
		this.router.navigate([this.paramsService.subjectId()]);
	}

	closeMainMenu() {
		this.menuService.mainMenuClosed.set(true);
	}

	openMainMenu() {
		this.menuService.mainMenuClosed.set(!this.menuService.mainMenuClosed());
	}

	clickThemeHeader() {
		this.menuService.showTheme.set(!this.menuService.showTheme());
	}

	clickWeek(weekId: number) {
		if (this.mobileService.mobileView()) {
			this.menuService.mainMenuClosed.set(true);
		}
		this.menuService.currentPathId.set(weekId);
		const url = this.router.createUrlTree([this.paramsService.subjectId(), appRoutesType.TRAINING, weekId]).toString();
		this.location.replaceState(url);
		this.menuService.editAction({ action: this.menuActionType.OPEN_TRAINING, value: weekId.toString() });
	}

	floor(value: number): number {
		return Math.floor(value);
	}

	private getPaths(subjectId: number | undefined, navigateToTraining = false) {
		if (subjectId) {
			this.pathsService
				.getPath(subjectId)
				.pipe(
					catchError(() => {
						return of(null);
					})
				)
				.subscribe((paths) => {
					const isSubjectHome = this.router.url === `/${subjectId}`;
					if (paths?.weeks && paths.weeks.length > 0) {
						this.menuService.paths.set(paths);

						if (!this.currentWeek) {
							const sortedWeeks = [...paths.weeks].sort((a, b) => a.weekNumber - b.weekNumber);
							const currentWeek = sortedWeeks.find((week) => week.weekPosition === 0);
							if (currentWeek) {
								this.menuService.currentPathId.set(currentWeek.id);
							} else {
								const weekToGo =
									sortedWeeks[0].weekPosition === 1 ? sortedWeeks[0] : sortedWeeks[sortedWeeks.length - 1];
								this.menuService.currentPathId.set(weekToGo.id);
							}
						} else {
							this.menuService.currentPathId.set(this.currentWeek);
						}

						if (navigateToTraining) {
							this.router.navigate([
								this.paramsService.subjectId(),
								appRoutesType.TRAINING,
								this.menuService.currentPathId()
							]);
						} else if (isSubjectHome && !this.menuService.firstNavigated) {
							this.goRouteSection();
						}
					} else {
						this.menuService.paths.set(null);
						this.menuService.currentPathId.set(null);
					}
					if (!this.menuService.firstNavigated) {
						this.menuService.firstNavigated = true;
					}
				});
		}
	}

	private getChats(topicIds: number[] | null) {
		if (topicIds) {
			this.chatService
				.getChats(this.paramsService.subjectId(), topicIds)
				.pipe(
					catchError(() => {
						this.#messageService.sendMessageTranslate({
							type: 'error',
							title: 'ERRORS.CHAT.GET',
							text: 'ERRORS.CLOSE_RETRY',
							redirect: true
						});
						return of(null);
					})
				)
				.subscribe((chats) => {
					this.menuService.chatList.set(chats);
				});
		} else {
			this.menuService.chatList.set(null);
		}
	}

	private listenQueryParams() {
		const urlSegments = this.router.url.split('/');
		if (urlSegments.includes(appRoutesType.TRAINING)) {
			const index = urlSegments.indexOf(appRoutesType.TRAINING);
			const segment = urlSegments[index + 1];
			const id = Number(segment?.split('?')[0]);

			if (!isNaN(id)) {
				this.currentWeek = id;
			}
		}
	}

	private listenMenuService() {
		this.menuService.action$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
			if (action.action === this.menuActionType.REFRESH_CHAT_LIST) {
				this.getChats(this.paramsService.topicIds());
			} else if (action.action === this.menuActionType.GO_HOME) {
				if (this.router.url.includes(`/${appRoutesType.TRAINING}/`)) {
					this.goRouteSection();
				} else {
					this.goChatSection();
				}
			}
		});
	}
}
